import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { Form, notification, Button, Typography } from 'antd';

import * as globalSelectors from '@redux/global/selectors';
import * as productFullViewSelector from 'pages/product-full-view/controllers/selectors';
import memberReducer from 'pages/company-profile/controllers/reducer';
import saga from 'pages/company-profile/controllers/saga';

import { useInjectSaga } from 'utils/common/injectSaga';
import { useInjectReducer } from 'utils/common/injectedReducers';

import {
  invalidateBrandingNavigation,
  useCallbackRef,
  useCheckIsProductOverlay,
  useDeepCompareEffect,
  useGetProductEnums,
  useUserInfo,
} from 'hooks';

import * as productServices from 'services/product';
import * as ssoProductServices from 'services/sso-product';
import * as ssoProductSelectors from 'pages/sso-product/controllers/selectors';
import * as productFullViewActions from 'pages/product-full-view/controllers/actions';
import * as productFullViewSelectors from 'pages/product-full-view/controllers/selectors';

import { useIntl } from 'react-intl';
import messages from 'i18n/messages/product';
import {
  covertNestedArrToSingleArr,
  mappingProperties,
} from 'pages/minimum-requirements/components/modal/utils';
import { useGetEntityProperties } from './entityHooks';
import { useSsoProductParams } from 'hooks/useSso';
import { defaultType } from 'pages/qa-spec/constant';
import { getQaPackagingTabDataQueryKey } from 'pages/qa-spec/components/qa-packaging/queries';
import { useQueryClient } from '@tanstack/react-query';
import { useQueryProductCompanyInfo } from 'common/queries/product-module';
import { keyGetProductItemModules } from 'pages/product-full-view/shared/hooks';
import { useGetProductVersionList } from 'pages/product-history/hook';
import { useGetCurrentProductHierarchyDetails } from 'pages/product-full-view/hooks';
import { useGetProductImagesByStatus } from 'hooks/useGetProductImagesByStatus';

import {
  extractImageIds,
  mergeProductImagery,
  resetProductImageryStatus,
} from 'common/components/product-add/components/utils/images';
import { getParamsFromUrl } from 'utils/common/route';
import { getProductHierarchiesQueryKey } from './useProductHierarchies';
import { useQueryMarketingAssets } from 'pages/product-full-view/queries';
import { useGetProductItemModules } from 'pages/product-full-view/components/product-new-detail-view/queries';

import { EVENT } from 'static/Constants';
import {
  DEFAULT_PRODUCT_IMAGERY,
  DEFAULT_TARGET_MARKET,
  DEFAULT_UNIT_COUNT,
} from 'common/components/product-add/components/constants';
import {
  checkActiveStatus,
  isUnitPackageLevel,
} from 'common/components/product-add/components/utils/validation';
import { sleep } from 'utils/delay';
import { generateErrorFieldList } from 'pages/home/ribbon/components/sections/product/components/upload-template/utils';
import { useValidatePrimaryFieldsForActiveProduct } from 'hooks/useValidateSelfLoadedProduct';
import { CustomNotification } from 'common/components';
import { useCheckAllowEditPrivateLabel } from 'common/components/product-add/hooks';
import { useGetWalmartProductSchema } from 'pages/branded-products/components/post-to-walmart-krogger/queries';

const { Paragraph } = Typography;

export const useGetTargetMarketList = () => {
  const targetMarketList = useSelector(
    globalSelectors.selectProductTargetMarket()
  );

  return targetMarketList;
};

const useCreateProduct = (props) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const location = useLocation();

  useInjectReducer({ key: 'member', reducer: memberReducer });
  useInjectSaga({ key: 'member', saga });

  const {
    modalVisible,
    productDetail,
    isEdit,
    reloadPage = () => {},
    callApiCallback,
    memberId,
    invalidAllProductOriginFull,
    invalidateAllProductFull,
    productId,
    onUpdateValidateProducts,
    dialogConfirm,
    onShowModalUpdate,
    setProductData,
    productData,
    initialGtin,
  } = props;

  const { refetchDataProductModules } = useGetProductItemModules(
    productId,
    false //* isEnabled
  );

  const { refetch: refetchMarketingAssets } = useQueryMarketingAssets({
    productId,
  });

  const dispatch = useDispatch();

  const {
    userInfo: { member, id },
  } = useUserInfo();

  const [editBrandVisible, setEditBrandVisible] = useState(false);
  const [brandThumb, setBrandThumb] = useState();
  const [brandId, setBrandId] = useState(null);
  const [brick, setBrick] = useState();
  const [productCategory, setProductCategory] = useState(null);
  const [formMode, setFormMode] = useState(isEdit ? 'edit' : 'create');
  const [formLoading, setFormLoading] = useState(false);
  const [productImagery, setProductImagery] = useState(DEFAULT_PRODUCT_IMAGERY);
  const [validateErrors, setValidateErrors] = useState([]);

  const isOverlay = useCheckIsProductOverlay();

  const status = Form.useWatch('status', form) || productDetail?.status;

  const { productEnums } = useGetProductEnums();
  const currentHierarchy = useSelector(
    productFullViewSelector.makeSelectCurrentHierarchy()
  );
  const targetMarketList = useSelector(
    globalSelectors.selectProductTargetMarket()
  );

  const productHierarchyInfo = useSelector(
    productFullViewSelectors.makeSelectCurrentHierarchy()
  );

  const { data: companyInfoState, refetch: refetchProductCompanyInfoState } =
    useQueryProductCompanyInfo({
      productId: +productId,
    });

  const { checkAllowEditPrivateLabel } = useCheckAllowEditPrivateLabel({
    productData,
    isEdit,
    memberId,
  });

  const { invalidateWalmartProductSchema } = useGetWalmartProductSchema({
    enabled: false,
  });

  const { retailerId, supplierId, projectId } =
    getParamsFromUrl(location) ?? {};

  const validatePrimaryFieldsMutation =
    useValidatePrimaryFieldsForActiveProduct({
      form,
      setValidateErrors,
    });

  const { initialProductImagery, invalidateProductImagesByStatus } =
    useGetProductImagesByStatus({
      status,
      productId: productDetail?.productId,
      options: {
        enabled: !!status && !!productDetail?.productId && isEdit,
      },
    });

  const {
    newSupplier,
    supplierName,
    productId: ssoProductId,
    contactEmail,
    contactFirstName,
    contactLastName,
    contactPhoneNumber,
  } = useSelector(ssoProductSelectors.selectBpSsoProductData()) ?? {};

  const queryClient = useQueryClient();
  const { getEntityProperties } = useGetEntityProperties({
    entityType: 'Product',
    fetchOnMount: modalVisible,
  });

  const { invalidateProductHierarchyDetails } =
    useGetCurrentProductHierarchyDetails();

  const activeStatusWarningKey = 'status-active-warning';
  const currentBrandImgSrc = companyInfoState?.brandImage?.fileUrl;
  const brandImageId = companyInfoState?.brandImage?.id;

  const productTypeEnums = productEnums?.filter(
    (type) => type.enumName === 'ProductTypeEnum'
  );

  const { handleRefetchProductVersioning } = useGetProductVersionList({
    enabled: false,
  });

  const { isSsoProductView } = useSsoProductParams();

  const { invalidateMarketingAssets } = useQueryMarketingAssets({
    enabled: false,
  });

  const updateBrickCodeData = (brickCode) => {
    setBrick(brickCode);
    form.setFieldsValue({
      ...form.getFieldsValue(),
      brick: brickCode?.brickCode,
    });
  };

  const reloadProductHierarchies = () => {
    const id = productId || ssoProductId;

    queryClient.invalidateQueries({
      queryKey: getProductHierarchiesQueryKey(id),
    });
    if (currentHierarchy?.productHierarchyId)
      invalidateProductHierarchyDetails();
  };

  const notifyError = (msg) => {
    notification.error({
      message: msg ? msg : 'Something went wrong!',
    });
  };

  const callApiAddProduct = async (values) => {
    const brickCode = brick?.brickCode;
    const brickCodeName = brick?.brickCodeName;
    const visibility = values?.visibility ?? 'Private';
    const type = values?.type ?? defaultType;
    setFormLoading(true);

    const productImageryIds = extractImageIds(productImagery);

    const payload = {
      ...values,
      ...productCategory,
      brandId,
      brickCode,
      brickCodeName,
      visibility,
      memberId,
      type,
      ...productImageryIds,
    };

    try {
      const res = await productServices.addProduct(payload);
      const { isSuccess, data, message } = res;

      if (!isSuccess) {
        notifyError(message);
        return;
      }

      setProductData && setProductData(data);
      callApiSuccessCallback(data?.errorFields, data);
      setTimeout(() => reloadPage(), 300);

      // Handle active status validation
      const isActiveStatus = checkActiveStatus(payload?.status);
      const isPrivateLabel = data?.isPrivateLabel;

      const needsValidationUpdate =
        data?.activeResult !== null && data?.activeResult?.isSuccess !== true;

      const isActiveAndValid =
        isActiveStatus &&
        (data?.activeResult?.isSuccess === true || data?.activeResult === null);

      if (isActiveAndValid) {
        if (isPrivateLabel) return;
        onShowModalUpdate();
        return;
      }

      if (
        needsValidationUpdate &&
        !isPrivateLabel
      ) {
        setValidateErrors(data?.activeResult ?? {});
        dialogConfirm({
          onOk: () => {
            onShowModalUpdate();
          },
        });
        return;
      }
    } catch (err) {
      notifyError();
    } finally {
      setFormLoading(false);
    }
  };

  const getFormPayload = () => {
    const values = form.getFieldsValue(true);
    const brickCode = brick?.brickCode;
    const brickCodeName = brick?.brickCodeName;

    const id = productData?.productId;

    const brandImageId = brandId ? brandId : values?.brandId;
    const visibility = values?.visibility ?? productData.visibility;
    const status = values?.status ?? productData.status;

    const mappingProductImagery = extractImageIds(productImagery);

    let payload = {
      ...values,
      ...productCategory,
      ...mappingProductImagery,
      brandId: brandImageId,
      id,
      productId: id,
      brickCode,
      brickCodeName,
      visibility,
      status,
    };

    return payload;
  };

  const callApiEditProduct = (ixoneIgnore, fieldList) => {
    const values = form.getFieldsValue();

    const brickCode = brick?.brickCode;
    const brickCodeName = brick?.brickCodeName;

    const id = productData?.productId;
    const brandImageId = brandId ? brandId : values?.brandId;
    const visibility = values?.visibility ?? productData.visibility;
    const status = values?.status ?? productData.status;

    const mappingProductImagery = extractImageIds(productImagery);

    let payload = {
      ...values,
      ...productCategory,
      ...mappingProductImagery,
      brandId: brandImageId,
      id,
      brickCode,
      brickCodeName,
      visibility,
      status,
      ixOneIgnore: ixoneIgnore,
      IxOneIgnoreFields: ixoneIgnore === 'ignore' ? fieldList : undefined,
    };

    setFormLoading(true);

    if (isSsoProductView) {
      payload = Object.assign(payload, {
        retailerId: parseInt(retailerId),
        supplierId: parseInt(supplierId),
        supplierName,
        projectId,
        productId: +ssoProductId || id,
      });
      if (newSupplier === '1') {
        payload = Object.assign(payload, {
          newSupplier: true,
          contactEmail,
          contactFirstName,
          contactLastName,
          contactPhoneNumber,
        });
      }

      ssoProductServices
        .bpSsoUpdateProductDetailsService(payload)
        .then(async (res) => {
          const { isSuccess, message, data } = res;
          if (isSuccess) {
            reloadPage();
            setFormLoading(false);
            queryClient.invalidateQueries({
              queryKey: getQaPackagingTabDataQueryKey(id),
            });
            queryClient.invalidateQueries({
              queryKey: keyGetProductItemModules(id),
            });
            refetchDataProductModules();
            reloadProductHierarchies();
            invalidateWalmartProductSchema();
            handleRefetchProductVersioning();
            callApiSuccessCallback(data);
          } else {
            notifyError(message);
            setFormLoading(false);
          }
        })
        .catch((err) => {
          notifyError();
          setFormLoading(false);
        });
    } else {
      productServices
        .updateProductDetail(payload)
        .then(async (res) => {
          const { isSuccess, message, data } = res;
          if (isSuccess) {
            queryClient.invalidateQueries({
              queryKey: keyGetProductItemModules(id),
            });
            queryClient.invalidateQueries({
              queryKey: getQaPackagingTabDataQueryKey(id),
            });
            refetchDataProductModules();
            reloadProductHierarchies();
            handleRefetchProductVersioning();
            callApiSuccessCallback(data);
            refetchMarketingAssets();
            invalidateWalmartProductSchema();
            dispatch(
              productFullViewActions.getProductImagery({
                hierarchyId: productHierarchyInfo?.productHierarchyId,
                productId: id,
              })
            );
          } else {
            notifyError(message);
            resetFormState();
          }
        })
        .catch((err) => {
          notifyError();
        })
        .finally(() => {
          setFormLoading(false);
        });
    }
  };
  const submitForm = async (ixoneIgnore, fieldList) => {
    const status = form.getFieldValue('status');
    const isActiveStatus = checkActiveStatus(status);
    const isUnit = isUnitPackageLevel(form.getFieldValue('packageLevel'));

    try {
      let values;

      if (isActiveStatus && isUnit) {
        // Active status: Validate all fields
        values = await form.validateFields();
      } else {
        // Non-active status: Only validate productName and packageLevel
        await form.validateFields(['productName', 'packageLevel']);
        // Get all form values (including non-validated fields)
        values = form.getFieldsValue(true);
      }

      // Submit based on form mode
      if (formMode === 'edit') {
        callApiEditProduct(ixoneIgnore, fieldList);
      } else {
        callApiAddProduct(values);
      }
    } catch (error) {
      CustomNotification.error(
        'Please resolve all form errors before submitting'
      );
    }
  };

  const callApiSuccessCallback = (errorFields, newProductData) => {
    const successMessage =
      formMode === 'edit' ? 'updateProductSuccess' : 'createProductSuccess';
    notification.success({
      message: intl.formatMessage(messages[successMessage]),
    });

    //* overlay refetch api all product origin full
    invalidAllProductOriginFull && invalidAllProductOriginFull();
    //* overlay refetch api all product full
    invalidateAllProductFull && invalidateAllProductFull();

    invalidateBrandingNavigation(queryClient, id);

    handleResetBrandId();

    if (formMode === 'edit') {
      refetchProductCompanyInfoState();
      invalidateProductImagesByStatus();
      setProductImagery((prevImagery) =>
        resetProductImageryStatus(prevImagery)
      );
      invalidateMarketingAssets(productId);
      const reloadDocGrid = new CustomEvent(EVENT.RELOAD_DOC_GRID_IN_FULL_VIEW);
      document.dispatchEvent(reloadDocGrid);
    }

    callApiCallback(newProductData);
    resetFormState();

    const filteredErrors = errorFields?.activeResult?.errors.filter(
      (error) => !error.isWarning
    );

    const hasErrorFields =
      errorFields?.activeResult?.minimumRequirementErrorFields?.length > 0 ||
      filteredErrors?.length > 0;

    if (hasErrorFields > 0)
      showActiveStatusWarning(errorFields?.activeResult);
  };

  const handleResetBrandId = () => {
    setBrandThumb('');
    setBrandId('');
    form.setFieldsValue({ brandId: null });
    form.validateFields(['brandId']).catch(() => {});
  };

  const initFormState = useCallbackRef(async () => {
    if (formMode === 'edit') {
      const { category1, category2, category3, category4 } = productData || {};

      const isUnit = isUnitPackageLevel(productData?.packageLevel);
      const isActiveStatus = checkActiveStatus(productData?.status);

      const formValues = {
        ...productData,
        brick: productData?.brickCode,
        countryOfOrigin: productData?.countryOfOrigin ?? [],
        unitCount:
          productData?.unitCount ??
          (isActiveStatus ? DEFAULT_UNIT_COUNT : null),
        targetMarket:
          productData?.targetMarket ||
          (isActiveStatus ? DEFAULT_TARGET_MARKET : null),
        category1Id: category1?.categoryName,
        category2Id: category2?.categoryName,
        category3Id: category3?.categoryName,
        category4Id: category4?.categoryName,
      };

      form.setFieldsValue(formValues);

      setBrandThumb(currentBrandImgSrc);
      setBrandId(brandImageId);
      setBrick({
        brickCode: productData?.brickCode,
        brickCodeName: productData?.brickCodeName,
      });
      setProductCategory({
        category1Id: category1?.categoryId,
        category2Id: category2?.categoryId,
        category3Id: category3?.categoryId,
        category4Id: category4?.categoryId,
      });

      await sleep(300);
      // Validate fields to show errors on load for edit mode
      form.validateFields(['gtin', 'upc8', 'upc12', 'upc13']).catch(() => {
        // Errors will be displayed, but we don't stop execution
      });

      if (isUnit && isActiveStatus && !isOverlay) {
        form.validateFields().catch(() => {});
        const response =
          await validatePrimaryFieldsMutation.mutateAsync(formValues);
        if (response?.isSuccess) {
          onUpdateValidateProducts && onUpdateValidateProducts(response);
        }
      }

      return;
    }

    const isEditPrivateLabel = checkAllowEditPrivateLabel();
    const isManufacturer = member?.memberType === 'Manufacturer';

    //* create product
    form.setFieldsValue({
      status: 'Pending',
      visibility: isManufacturer ? 'Private' : 'Only If Shared',
      isPrivateLabel: isEditPrivateLabel,
    });

    if (initialGtin) {
      form.setFieldsValue({ gtin: initialGtin });
    }
  });

  const resetFormState = () => {
    form.resetFields();
    handleResetBrandId();
    setBrick('');
    formMode === 'create' && setProductImagery({});
    setValidateErrors({});
  };

  const updateFormMode = () => {
    setFormMode(isEdit ? 'edit' : 'create');
  };

  const showActiveStatusWarning = (errorFields) => {
    const entityProperties = getEntityProperties();
    const { properties } = covertNestedArrToSingleArr(entityProperties);

    const propertiesErrorField =
      generateErrorFieldList([errorFields], properties) || [];

    const warningContent = (
      <Paragraph>
        The product has not passed data validations, and can not be set to
        Active at this time. The data has been saved, and the product set to a
        Pending status.
      </Paragraph>
    );

    const groupRequiredField = mappingProperties(propertiesErrorField);

    const FieldList = (
      <ul className='add-product__active-status-waring-list scroller'>
        {groupRequiredField.map((properties, index) => {
          return (
            <div key={index}>
              <p className='active-status-waring-list__title'>
                {properties.moduleDisplayName}:
              </p>
              {properties?.moduleProperties.map((item, index) => {
                return (
                  <li
                    style={{
                      paddingLeft: 10,
                      fontWeight: 600,
                      lineHeight: 1.25,
                      marginBottom: 5,
                    }}
                    key={index}
                  >
                    {item?.errorMessage
                      ? item?.errorMessage
                      : `${
                          item?.fullPathDisplayName || item?.displayName
                        } is required`}
                  </li>
                );
              })}
            </div>
          );
        })}
      </ul>
    );

    const CloseBtn = (
      <Button
        type='primary'
        onClick={() => notification.close(activeStatusWarningKey)}
      >
        OK
      </Button>
    );
    notification.warn({
      message: warningContent,
      description: FieldList,
      duration: 0,
      key: activeStatusWarningKey,
      btn: CloseBtn,
    });

    form.setFields([
      {
        name: 'status',
        errors: [intl.formatMessage(messages.updateStatusActiveError)],
      },
    ]);
  };

  const productDataJSON = JSON.stringify(productData);

  useEffect(() => {
    initFormState();
  }, [modalVisible, productDataJSON, initFormState]); // if we don't add productData as a dependency. After making active product, new product full will not be updated

  // Update imagery from API, but ONLY for fields that are currently null
  useDeepCompareEffect(() => {
    if (formMode === 'edit' && initialProductImagery) {
      setProductImagery((prevValue) =>
        mergeProductImagery(prevValue, initialProductImagery)
      );
    }
  }, [initialProductImagery, formMode]);

  useEffect(() => {
    updateFormMode();
  }, [isEdit]);

  useEffect(() => {
    if (isEdit) {
      productDetail && setProductData && setProductData(productDetail);
    }
  }, [JSON.stringify(productDetail), isEdit, setProductData]);

  useEffect(() => {
    //* After editing product brand by creating new brand or editing current brand
    //* productFullViewActions.getProductCompanyInfo are called to reload product brand info
    //* this effect will update brandThumb with new product brand info thumb
    if (formMode === 'edit') {
      setBrandThumb(currentBrandImgSrc);
    }
  }, [currentBrandImgSrc, formMode]);

  return {
    submitForm,
    resetFormState,
    formLoading,
    form,
    productData,
    formMode,
    targetMarketList,
    productEnums,
    currentHierarchy,
    brick,
    setBrick,
    updateBrickCodeData,
    handleResetBrandId,
    brandId,
    setBrandId,
    editBrandVisible,
    setEditBrandVisible,
    brandThumb,
    setBrandThumb,
    productTypeEnums,
    productCategory,
    setProductCategory,
    setProductImagery,
    productImagery,
    getFormPayload,
    validateErrors,
    setValidateErrors,
    initialGtin,
  };
};

export default useCreateProduct;
